// frontend/src/utils/PermissionContext.js
//
// Capability lookup for any component, without threading `authUser` through
// props.
//
// Most admin screens are routed as `<AdminThing t={t} />` and never received
// `authUser` at all, so gating their write controls used to mean editing
// App.js for every screen and remembering to do it again for the next one.
// A context means a new screen asks `can(CAP.ADMIN_WRITE)` and gets the right
// answer with no wiring.
//
// This is presentation only. Every endpoint behind these controls is
// authorized server-side by backend/route_guard.py; hiding a button is a
// courtesy to the user, never the enforcement.

import React, { createContext, useContext, useMemo } from 'react';
import { CAP, can as canFor } from './permissions';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ authUser, children }) => {
    const value = useMemo(
        () => ({
            authUser,
            role: authUser?.role || null,
            can: (capability) => canFor(authUser, capability),
        }),
        [authUser]
    );
    return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * `const { can } = usePermissions();` then `can(CAP.ADMIN_WRITE)`.
 *
 * Fails closed when no provider is above it: a component rendered outside the
 * tree (a test, a stray render) shows no write controls rather than all of
 * them.
 */
export const usePermissions = () => useContext(PermissionContext) || {
    authUser: null,
    role: null,
    can: () => false,
};

/**
 * Drops `hover:` variants from a Tailwind class string when a control is inert.
 *
 * `:hover` still matches a `disabled` element — only clicks are suppressed — so
 * a status pill kept visible for a Read-only account would otherwise still
 * change colour under the cursor and go on advertising itself as a button.
 * Written as a filter rather than as two hand-maintained class strings so the
 * colours stay defined once.
 */
export const inertClasses = (classes, inert) =>
    inert
        ? (classes || '').split(/\s+/).filter((c) => !c.startsWith('hover:')).join(' ')
        : classes;

/**
 * Banner for an admin screen the user can read but not act on.
 *
 * Without it, an account that can read a page but not act on it sees an action
 * column that is simply empty, and cannot tell whether that is a permission or
 * a bug. Renders nothing for anyone who can write, so it can be dropped into
 * every admin screen unconditionally.
 *
 * On an admin screen the audience is the User role: Read-only no longer reaches
 * those pages at all. Worded about the page rather than about the account,
 * because User *can* act — just not here: it may place orders on the dashboard
 * and edit customer profiles, and holds nothing that writes on an admin screen.
 *
 * `capability` is the one this page's actions actually need. It defaults to
 * ADMIN_WRITE, which is right for an admin screen, but a page gated on
 * something else must say so — the customer profile form needs
 * WRITE_CUSTOMER_PROFILE, and showing "you cannot change anything here" to a
 * User who can in fact edit that form would be a lie.
 */
export const ReadOnlyNotice = ({ t, capability = CAP.ADMIN_WRITE, message }) => {
    const { can } = usePermissions();
    if (can(capability)) return null;
    return (
        <div className="mb-4 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600 flex items-start gap-2">
            <span aria-hidden="true">👁️</span>
            <span>
                {message
                    || t?.readonly_notice
                    || 'You can view everything on this page, but you do not have permission to make changes here, so actions that change data are hidden.'}
            </span>
        </div>
    );
};
